import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!token) {
    return (
      <Layout noFooter>
        <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center p-8 md:p-16 bg-linen">
          <div className="w-full max-w-md text-center" data-testid="reset-no-token">
            <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-lilac border-2 border-black flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-black" strokeWidth={1.5} />
            </div>
            <h1 className="font-serif text-4xl text-wine mb-3 leading-tight">This link looks broken.</h1>
            <p className="text-ink/70 mb-7">Request a new reset link to continue.</p>
            <Link to="/forgot-password" className="qld-btn-primary inline-flex" data-testid="reset-request-new">
              Request a new link <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw !== pw2)    { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { data: user } = await api.post("/auth/reset", { token, new_password: pw });
      if (setUser) setUser(user);
      const dest = user.role === "admin" ? "/dashboard/admin"
                : user.role === "vendor" ? "/dashboard/vendor"
                : "/dashboard/lovers";
      navigate(dest, { replace: true });
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout noFooter>
      <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center p-8 md:p-16 bg-linen">
        <div className="w-full max-w-md">
          <div className="w-12 h-12 mb-5 rounded-full bg-mustard border-2 border-black flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5 text-black" strokeWidth={1.5} />
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-wine mb-2">Choose a new password</h1>
          <p className="text-wine/70 mb-10">
            Pick something only you would know. Eight characters minimum.
          </p>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">New password</label>
              <input
                data-testid="reset-pw-input"
                type="password"
                required
                minLength={8}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="qld-input"
                placeholder="••••••••"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">Confirm new password</label>
              <input
                data-testid="reset-pw2-input"
                type="password"
                required
                minLength={8}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                className="qld-input"
                placeholder="••••••••"
              />
            </div>
            {err && <p data-testid="reset-error" className="text-wine text-sm">{err}</p>}
            <button
              data-testid="reset-submit-btn"
              type="submit"
              disabled={busy}
              className="qld-btn-primary w-full"
            >
              {busy ? "Updating…" : <>Update password <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></>}
            </button>
          </form>
          <p className="text-sm text-muted text-center mt-8">
            <Link to="/login" className="qld-link" data-testid="reset-to-login">Back to log in</Link>
          </p>
        </div>
      </div>
    </Layout>
  );
}
